import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COUNTY_OF_ARREST_OPTIONS } from '../config/constants';
import { API_ENDPOINTS } from '../config/api';

const HomePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    role: '',
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    jailId: '',
    policeReportNumber: '',
    fileNumber: '',
    countyOfArrest: '',
    saNumber: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case 'role':
        if (!value) {
          newErrors.role = 'Please select your role';
        } else {
          delete newErrors.role;
        }
        break;
      case 'countyOfArrest':
        if (!value) {
          newErrors.countyOfArrest = 'Please select a county of arrest';
        } else {
          delete newErrors.countyOfArrest;
        }
        break;
      case 'firstName':
        if (!value.trim()) {
          newErrors.firstName = 'First name is required';
        } else {
          delete newErrors.firstName;
        }
        break;
      case 'lastName':
        if (!value.trim()) {
          newErrors.lastName = 'Last name is required';
        } else {
          delete newErrors.lastName;
        }
        break;
      case 'dateOfBirth':
        if (!value) {
          newErrors.dateOfBirth = 'Date of birth is required';
        } else {
          delete newErrors.dateOfBirth;
        }
        break;
      case 'email':
        if (!value) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const validateForm = () => {
    const requiredFields = [
      'role',
      'firstName',
      'lastName',
      'dateOfBirth',
      'email',
      'countyOfArrest',
    ];
    let newErrors = {};
    let newTouched = {};
    requiredFields.forEach((field) => {
      newTouched[field] = true;
      const value = formData[field];
      if (!value) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())} is required`;
      }
      if (field === 'email' && value && !/\S+@\S+\.\S+/.test(value)) {
        newErrors.email = 'Please enter a valid email address';
      }
    });
    setErrors(newErrors);
    setTouched(newTouched);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setSubmitting(true);
  setSubmitError('');

  try {
    const payload = {
      role: formData.role,
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim() || null,
      lastName: formData.lastName.trim(),
      dateOfBirth: formData.dateOfBirth,
      email: formData.email.trim(),
      jailId: formData.jailId.trim() || null,
      policeReportNumber: formData.policeReportNumber.trim() || null,
      fileNumber: formData.fileNumber.trim() || null,
      countyOfArrest: formData.countyOfArrest || null,
      saNumber: formData.saNumber.trim() || null,
    };

    const response = await fetch(API_ENDPOINTS.SUBMIT_APPLICATION, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const msg = await response.text().catch(() => `HTTP ${response.status}`);
      throw new Error(msg || `Server error (HTTP ${response.status})`);
    }

    const caseNumber = await response.text();
    navigate('/submitted', { state: { caseNumber: caseNumber.replace(/"/g, '') } });
  } catch (err) {
    console.error('[HomePage] submit failed:', err);
    setSubmitError(`Submission failed: ${err.message}`);
  } finally {
    setSubmitting(false);
  }
};

  const isRoleSelected = !!formData.role;

  return (
    <div className="home-page">
      <div className="form-container">
        <div className="form-header">
          <h1>DUI First Time Offender Program Form</h1>
          <p>Please complete all required fields marked with an asterisk (*) to proceed.</p>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Role Selection */}
          <div className="form-section">
            <label htmlFor="role">
              Select Your Role <span className="required">*</span>
            </label>
            <div className="select-wrapper">
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.role && touched.role ? 'error-border' : ''}
                aria-describedby="role-error"
              >
                <option value="" disabled>
                  Please select...
                </option>
                <option value="Defendant">
                  I am the defendant
                </option>
                <option value="Representative">
                  I represent the defendant
                </option>
              </select>
              <span className="select-icon">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </div>
            {errors.role && touched.role && (
              <p className="error" id="role-error">
                {errors.role}
              </p>
            )}
          </div>

          {/* Defendant Information Section */}
          <div className="form-section section-box">
            <h2>Defendant Information</h2>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter first name"
                  disabled={!isRoleSelected}
                  className={errors.firstName && touched.firstName ? 'error-border' : ''}
                  aria-describedby="firstName-error"
                />
                {errors.firstName && touched.firstName && (
                  <p className="error" id="firstName-error">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="middleName">
                  Middle Name
                </label>
                <input
                  type="text"
                  id="middleName"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  placeholder="Enter middle name (optional)"
                  disabled={!isRoleSelected}
                />
              </div>
              <div className="form-field">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter last name"
                  disabled={!isRoleSelected}
                  className={errors.lastName && touched.lastName ? 'error-border' : ''}
                  aria-describedby="lastName-error"
                />
                {errors.lastName && touched.lastName && (
                  <p className="error" id="lastName-error">
                    {errors.lastName}
                  </p>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="dateOfBirth">
                  Date of Birth <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!isRoleSelected}
                  className={errors.dateOfBirth && touched.dateOfBirth ? 'error-border' : ''}
                  aria-describedby="dateOfBirth-error"
                />
                {errors.dateOfBirth && touched.dateOfBirth && (
                  <p className="error" id="dateOfBirth-error">
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>
              <div className="form-field email-field">
                <label htmlFor="email">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="example@email.com"
                  disabled={!isRoleSelected}
                  className={errors.email && touched.email ? 'error-border' : ''}
                  aria-describedby="email-error"
                />
                {errors.email && touched.email && (
                  <p className="error" id="email-error">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Case Information Section */}
          <div className="form-section section-box">
            <h2>Case Information</h2>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="jailId">
                  Jail ID
                </label>
                <input
                  type="text"
                  id="jailId"
                  name="jailId"
                  value={formData.jailId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter jail ID"
                  disabled={!isRoleSelected}
                />
                <p className="field-note">
                  You can find your Jail ID on your booking paperwork.
                </p>
              </div>
              <div className="form-field">
                <label htmlFor="policeReportNumber">
                  Police Report Number
                </label>
                <input
                  type="text"
                  id="policeReportNumber"
                  name="policeReportNumber"
                  value={formData.policeReportNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter police report number"
                  disabled={!isRoleSelected}
                />
                <p className="field-note">
                  Found on your arrest documentation.
                </p>
              </div>
              <div className="form-field">
                <label htmlFor="fileNumber">
                  File Number
                </label>
                <input
                  type="text"
                  id="fileNumber"
                  name="fileNumber"
                  value={formData.fileNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter file number"
                  disabled={!isRoleSelected}
                />
                <p className="field-note">
                  Your court case file number.
                </p>
              </div>
              <div className="form-field">
                <label htmlFor="countyOfArrest">
                  County of Arrest <span className="required">*</span>
                </label>
                <div className="select-wrapper">
                  <select
                    id="countyOfArrest"
                    name="countyOfArrest"
                    value={formData.countyOfArrest}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!isRoleSelected}
                    className={errors.countyOfArrest && touched.countyOfArrest ? 'error-border' : ''}
                    aria-describedby="countyOfArrest-error"
                  >
                    <option value="" disabled>
                      Please select...
                    </option>
                    {COUNTY_OF_ARREST_OPTIONS.map((county) => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                  <span className="select-icon">
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
                {errors.countyOfArrest && touched.countyOfArrest && (
                  <p className="error" id="countyOfArrest-error">
                    {errors.countyOfArrest}
                  </p>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="saNumber">
                  SA Number
                </label>
                <input
                  type="text"
                  id="saNumber"
                  name="saNumber"
                  value={formData.saNumber}
                  onChange={handleChange}
                  placeholder="Enter SA number (optional)"
                  disabled={!isRoleSelected}
                />
                <p className="field-note">
                  Your State Attorney case number, if available.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {submitError && (
            <div style={{
              backgroundColor: '#ffe6e6',
              border: '1px solid #ff9999',
              borderRadius: '4px',
              padding: '1rem',
              margin: '1rem 0',
              color: '#721c24'
            }}>
              <p>{submitError}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" disabled={!isRoleSelected || submitting}>
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HomePage;